import React, { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import DateInput from './date-input'
import TimeInput from './time-input'
import Tooltip from './tooltip'
import WarningModal from '../components/warning-modal'
import updateAssignment from '../mutations/update-assignment'
import deleteAssignment from '../mutations/delete-assignment'
import { dateFromISO8601 } from '../utils'
import {
  addMinutes,
  addDays,
  setMinutes,
  setHours,
  getHours,
  getMinutes,
} from 'date-fns'

function AssignmentForm(props) {
  const currentTime = new Date()
  const assignedAtTest =
    (props.assignedAt && dateFromISO8601(props.assignedAt)) || currentTime
  const dueAtTest = props.dueAt && dateFromISO8601(props.dueAt)
  const assignedAtDefault = currentTime
  const editMode = props.dueAt && props.assignedAt

  const [allowLate, setAllowLate] = useState(props.allowLate || true)
  const [assignedAt, setAssignedAt] = useState(assignedAtTest)
  const [dueAt, setDueAt] = useState(
    dueAtTest || addMinutes(assignedAtDefault, 15)
  )
  const [requireEvidence, setRequireEvidence] = useState(props.requireEvidence)
  const [notifyStudents, setNotifyStudents] = useState(
    props.notifyStudents || false
  )
  const [assignNow, setAssignNow] = useState(props.assignedAt === null)
  const [assignLater, setAssignLater] = useState(props.assignedAt !== null)
  const [dateAssigned, setDateAssigned] = useState(props.assignedAt !== null)
  const [timeAssigned, setTimeAssigned] = useState(props.assignedAt !== null)
  const [dateDueAssigned, setDateDueAssigned] = useState(props.dueAt !== null)
  const [timeDueAssigned, setTimeDueAssigned] = useState(props.dueAt !== null)
  const [validAssignment, setValidAssignment] = useState(true)
  const [warningOpen, setWarningOpen] = useState(false)
  const [notifyStudentsDueChange, setNotifyStudentsDueChange] = useState(false)
  const [missingDueTime, setMissingDueTime] = useState(false)
  const [unassignPrompt, setUnassignPrompt] = useState(false)

  const [deleteAssignmentMutation] = useMutation(deleteAssignment)
  const [updateAssignmentMutation] = useMutation(updateAssignment)

  useEffect(() => {
    // const assignedAt = props.assignedAt && dateFromISO8601(props.assignedAt)
    // const dueAt = props.dueAt && dateFromISO8601(props.dueAt)
    // setAllowLate(true) // or setAllowLate(props.allowLate); if needed
    // setAssignedAt(assignedAt)
    // setDueAt(dueAt)
    // setRequireEvidence(props.requireEvidence)
    handleAssignNow()
  }, []) // The effect will run when any prop changes

  const handleSave = () => {
    const { data, id, onCancel, onMetaSave, metaReflection } = props

    if (unassignPrompt) {
      deleteAssignmentMutation({
        variables: { id },
      }).then(() => {
        onCancel()
        data.refetch()
      })
      return
    }

    const variables = {
      id,
      allowLate,
      requireEvidence,
      notifyStudents,
      notifyStudentsDueChange,
    }
    if (assignedAt) {
      variables.assignedAt = assignedAt
    }
    if (dueAt) {
      variables.dueAt = dueAt
    }

    if (!validateAssignment()) {
      setWarningOpen(true)
      return
    }

    updateAssignmentMutation({
      variables,
    }).then((data) => {
      if (data.payload && data.payload.errors) {
        // TODO: display errors
        console.log(data.payload.errors)
      } else {
        if (metaReflection && assignedAt && dueAt) {
          onMetaSave()
        } else {
          onCancel()
        }
      }
    })
  }

  const handleTimeAssignedAt = (hrs, mins) => {
    let newassignedAt = setHours(assignedAt, hrs)
    newassignedAt = setMinutes(newassignedAt, mins)
    setTimeAssigned(true)
    handleAssignedAt(newassignedAt, dateAssigned)
  }

  const handleAssignedAt = (d, dateAssigned = true) => {
    setAssignedAt(d)
    setDateAssigned(dateAssigned)
  }

  // const handleCancelComplete = () => {
  //   props.onCancel()
  // }

  const handleTimeDueAt = (hrs, mins) => {
    let newDueAt = setHours(dueAt, hrs)
    newDueAt = setMinutes(newDueAt, mins)
    setTimeDueAssigned(true)
    setDateAssigned(true)
    setDueAt(newDueAt)
  }

  const handleDueAt = (d, dateDueAssigned = true) => {
    setDueAt(d)
    setDateDueAssigned(dateDueAssigned)
  }

  const handleAssignNow = () => {
    if (editMode) {
      return
    }
    if (assignNow === false) {
      const mins = getMinutes(currentTime)
      const hrs = getHours(currentTime)
      handleTimeAssignedAt(hrs, mins)
      handleAssignedAt(currentTime)
      setAssignNow(!assignNow)
      setAssignLater(assignNow)
      setTimeAssigned(false)
      setDateAssigned(false)
    }
  }

  const handleAssignLater = () => {
    if (editMode) {
      return
    }
    if (assignLater === false) {
      setAssignLater(!assignLater)
      setAssignNow(assignLater)
    }
  }

  const toggleAllowLate = () => {
    // TODO: handle allow late
    // setAllowLate(!allowLate)
  }

  const toggleNotifyStudents = (e) => {
    e && e.preventDefault()
    setNotifyStudents(!notifyStudents)
  }

  const toggleRequireEvidence = (e) => {
    e && e.preventDefault()
    setRequireEvidence(!requireEvidence)
  }

  const toggleNotifyStudentsDueChange = (e) => {
    e && e.preventDefault()
    setNotifyStudentsDueChange(!notifyStudentsDueChange)
  }

  const toggleUnassignPrompt = (e) => {
    e && e.preventDefault()
    setUnassignPrompt(!unassignPrompt)
  }

  const validateAssignment = () => {
    let validAssignment = true
    const dueAtFloor = addMinutes(assignedAt, 15)

    // if it is an edit we only care about the due date being after the assigned date
    if (editMode && dueAt.getTime() < dueAtFloor.getTime()) {
      console.log('Due date is before assigned at + 15')
      return false
    }

    if (timeDueAssigned === false) {
      console.log('Missing time Due?', timeDueAssigned)
      setMissingDueTime(true)
      validAssignment = false
    }

    const currentTimeNow = new Date()
    if (
      (assignNow &&
        dateDueAssigned &&
        timeDueAssigned &&
        assignedAt.getTime() > currentTimeNow.getTime()) ||
      dueAt.getTime() < dueAtFloor.getTime()
    ) {
      setMissingDueTime(false)
      validAssignment = false
    }

    if (
      (dateAssigned &&
        timeAssigned &&
        dateDueAssigned &&
        timeDueAssigned &&
        assignedAt.getTime() < currentTimeNow.getTime()) ||
      dueAt.getTime() < dueAtFloor.getTime()
    ) {
      console.log('catch all?')
      setMissingDueTime(false)
      validAssignment = false
    }

    return validAssignment
  }

  const toggleWarning = (e) => {
    e && e.preventDefault()
    setWarningOpen(!warningOpen)
  }

  const { question, onCancel, metaReflection, submittedAnswers } = props
  const { text } = question

  return (
    <div className="assignment-form">
      <div className="heading">
        <h1>{text}</h1>
      </div>
      {metaReflection && (
        <div className="warning">
          <i className="material-icons">info</i>
          <span>
            Meta-reflections are assigned at the end of a journal. They
            encourage a look-back at previous reflections to build deeper
            insight.
          </span>
        </div>
      )}
      {!unassignPrompt && (
        <section>
          <div className="now-or-later">
            <div className="field now-or-later small-label">
              {editMode && 'Assigned to students'}
              {!editMode && 'Assign to students'}
            </div>
            <div
              className="field now-or-later-inputs"
              onClick={handleAssignNow}
            >
              {!editMode && (
                <section>
                  <input
                    type="checkbox"
                    name="assign-now"
                    id="assign-now"
                    checked={assignNow}
                    readOnly={editMode}
                  />
                  <label htmlFor="assign-now">Now</label>
                </section>
              )}
            </div>
            <div
              className="field now-or-later-inputs"
              onClick={handleAssignLater}
            >
              {!editMode && (
                <section>
                  <input
                    type="checkbox"
                    name="assign-later"
                    id="assign-later"
                    checked={assignLater}
                    readOnly={editMode}
                  />
                  <label htmlFor="assign-later">Schedule</label>
                </section>
              )}
            </div>
          </div>
          <div className="fields">
            <div className="assignment-datetime">
              <DateInput
                datetime={assignedAt}
                min={assignedAtDefault}
                max={addDays(assignedAtDefault, 8)}
                onChange={handleAssignedAt}
                readOnly={editMode || assignNow}
                dateAssigned={dateAssigned}
              />
              <TimeInput
                datetime={assignedAt}
                onChange={handleTimeAssignedAt}
                readOnly={editMode || assignNow}
                timeAssigned={timeAssigned}
              />
            </div>
            <div className="now-or-later">
              <div className="field due-date small-label">
                {editMode && 'New due date for responses'}
                {!editMode && 'Due date for responses'}
              </div>
            </div>
            <div className="assignment-datetime">
              <DateInput
                datetime={dueAt}
                min={addMinutes(assignedAt || assignedAtDefault, 15)}
                dateAssigned
                readOnly={assignNow}
                dateDueAssigned={dateDueAssigned}
                onChange={handleDueAt}
                required
              />
              <TimeInput
                datetime={dueAt}
                onChange={handleTimeDueAt}
                readOnly={assignNow}
                timeAssigned={timeDueAssigned}
                timeDueAssigned={timeDueAssigned}
              />
            </div>
            {editMode && (
              <section>
                <div
                  className="field inline"
                  onClick={toggleNotifyStudentsDueChange}
                >
                  <input
                    type="checkbox"
                    name="notify-students-due"
                    id="notify-students-due"
                    checked={notifyStudentsDueChange}
                  />
                  <label htmlFor="notify-students">
                    Notify students by e-mail that due date has changed.
                  </label>
                </div>
              </section>
            )}
            {!editMode && (
              <section>
                <div className="field inline" onClick={toggleNotifyStudents}>
                  <input
                    type="checkbox"
                    name="notify-students"
                    id="notify-students"
                    checked={notifyStudents}
                  />
                  <label htmlFor="notify-students">
                    Notify students by e-mail
                    <Tooltip>
                      Students will get an e-mail when the question is assigned.
                      They will also get a reminder 24 hours before the due
                      date.
                    </Tooltip>
                  </label>
                </div>
                <div className="field inline" onClick={toggleRequireEvidence}>
                  <input
                    type="checkbox"
                    name="require-evidence"
                    id="require-evidence"
                    checked={requireEvidence}
                  />
                  <label htmlFor="require-evidence">
                    Require students to attach evidence (PDF)
                  </label>
                </div>
                <div className="field inline" onClick={toggleAllowLate}>
                  <input
                    type="checkbox"
                    name="allow-late"
                    id="allow-late"
                    checked={allowLate}
                    readOnly
                  />
                  <label htmlFor="allow-late">
                    Allow late responses
                    <Tooltip>
                      Currently locked in ON position. Students can submit
                      reflections after the due date, but will be marked "late"
                      in ReflEQ.
                    </Tooltip>
                  </label>
                </div>
              </section>
            )}
          </div>
        </section>
      )}
      <div className="fields">
        {editMode && (
          <section>
            {submittedAnswers.length === 0 && (
              <div className="field inline" onClick={toggleUnassignPrompt}>
                <input
                  type="checkbox"
                  name="unassign-prompt"
                  id="unassign-prompt"
                  checked={unassignPrompt}
                />
                <label htmlFor="unassign-prompt">Un-assign prompt</label>
              </div>
            )}
          </section>
        )}
      </div>
      <div className="actions">
        <button onClick={handleSave}>
          {editMode && 'Save'}
          {!editMode && 'Assign'}
        </button>
        <a className="cancel" onClick={onCancel}>
          Cancel
        </a>
      </div>
      <WarningModal isOpen={warningOpen} onClose={toggleWarning}>
        <h2>Assignment Dates are not valid</h2>
        {!missingDueTime && (
          <p style={{ marginTop: 48, marginBottom: 54 }}>
            Assign date must not be in the past.
            <br />
            Due date must be at least 15 minutes after the Assign date.
          </p>
        )}
        {missingDueTime && (
          <p style={{ marginTop: 48, marginBottom: 54 }}>
            You have selected a Due date but not a Time. <br />
            Please select a Time.
          </p>
        )}
      </WarningModal>
    </div>
  )
}

export default AssignmentForm
